import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Block, DefaultText, FullScreenSpinner, MediumText, UpdateIcon } from '@guryou/html-components';
import { saveServicePlusSubService, stateIsLoading, useEffectOnError, useEffectOnSuccess, useTimeSlotsForSlider } from '@guryou/redux';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import CalendarDuration, { HoursExample } from 'onboard/components/CalendarDuration';
import OnboardingSlider from 'onboard/components/OnboardingSlider';
import CheckboxInfoContainer from 'onboard/containers/CheckboxInfoContainer';
import FormContainer from 'onboard/containers/FormContainer';
import InfoContainer from 'onboard/containers/InfoContainer';
import MainContainer from 'onboard/containers/MainContainer';
import PlayGuideVideoEmbed from 'onboard/containers/PlayGuideVideoEmbed';
import TitleContainer from 'onboard/containers/TitleContainer';
import { useOnboardContext } from 'onboard/context';
import { guideVideos } from 'onboard/GuideVideosUrls';
import { dealTokens, SystemFormGrid, SystemPriceInput } from 'components/shared';

import StepButtons from '../StepButtons';

const ServiceFinalDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { serviceState, resetServiceStep, setActiveStep } = useOnboardContext();
  const createService = useSelector(state => state.subServices.createServicePlusSubservice);

  const [value, setValue] = useState(serviceState.duration || 60);
  const [timeStep, setTimeStep] = useState(60);
  const [preparationTime, setPreparationTime] = useState(serviceState.preparationTime || 0);
  const marks = useTimeSlotsForSlider();

  const handleValueChange = val => {
    setValue(val);
    if (preparationTime > val) {
      setPreparationTime(Math.max(val, 0));
    }
  };

  const handlePreparationTime = val => {
    setPreparationTime(val);
  };

  const handleTimeStepChange = val => {
    const value = +val;
    setTimeStep(value);
  };

  useEffectOnSuccess(
    state => state.subServices.createServicePlusSubservice,
    () => {
      resetServiceStep(3);
    }
  );

  useEffectOnError(
    state => state.subServices.createServicePlusSubservice,
    () => {
      setActiveStep(0);
    }
  );

  const handleSubmit = data => {
    const { location, ...restState } = serviceState;
    dispatch(saveServicePlusSubService({ ...restState, ...data, ...location }));
  };

  if (stateIsLoading(createService)) {
    return <FullScreenSpinner />;
  }

  return (
    <MainContainer>
      <FormContainer handleSubmit={handleSubmit} parserVersion={2}>
        <TitleContainer title={t('ONBOARD.serviceFinalDetails')} />
        <Block lg={10} md={12} xs={12} margin={0} marginTop={6}>
          <SystemFormGrid singleColumn>
            <SystemPriceInput name="price" label={t('ONBOARD.price')} defaultValue={serviceState.price} required />
          </SystemFormGrid>
          <OnboardingSlider
            name="duration"
            label={t('ONBOARD.duration')}
            defaultValue={serviceState.duration}
            value={value}
            min={5}
            step={5}
            max={600}
            maxSlider={120}
            icon={<AccessTimeIcon />}
            margin={'20px 0px 20px 0px'}
            onChange={handleValueChange}
          />
          <OnboardingSlider
            name="preparationTime"
            label={t('ONBOARD.preparationTime')}
            min={0}
            value={preparationTime}
            defaultValue={preparationTime}
            step={5}
            max={value}
            maxSlider={value}
            icon={<UpdateIcon />}
            margin={'20px 0px 20px 0px'}
            onChange={handlePreparationTime}
          />
          <OnboardingSlider
            name="defaultTimeSlotStep"
            label={t('ONBOARD.timeSlot')}
            marks={marks}
            min={5}
            defaultValue={serviceState.defaultTimeSlotStep}
            max={120}
            maxSlider={120}
            step={null}
            icon={<CalendarTodayIcon />}
            margin={'20px 0px 20px 0px'}
            onChange={handleTimeStepChange}
          />

          <HoursExample timeStep={timeStep} />
        </Block>
        <StepButtons finish={true} />
      </FormContainer>
      <Block lg={5.6} md={5.8} xs={12} padding={0} margin={0}>
        <InfoContainer nested>
          <MediumText lineHeight="1.3">{t('ONBOARD_TEXT.serviceFinalDetailsTitle')}</MediumText>
          <DefaultText color={dealTokens.color.textMuted} marginTop={2}>
            {t('ONBOARD_TEXT.serviceFinalDetailsSubtitle')}
          </DefaultText>

          <div style={{ marginTop: dealTokens.space[6], paddingTop: dealTokens.space[5], borderTop: `1px solid ${dealTokens.color.border}` }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: dealTokens.font.weightSemibold,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: dealTokens.color.textMuted,
                marginBottom: dealTokens.space[4],
              }}
            >
              {t('ONBOARD_TEXT.serviceFinalDetailsCheckboxTitle')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: dealTokens.space[4] }}>
              <CheckboxInfoContainer text={t('ONBOARD_TEXT.serviceFinalDetailsCheckbox1Text')} />
              <CheckboxInfoContainer text={t('ONBOARD_TEXT.serviceFinalDetailsCheckbox2Text')} />
            </div>
          </div>

          <PlayGuideVideoEmbed videoId={guideVideos.serviceFinalDetails.videoId} modalTitle={guideVideos.serviceFinalDetails.title} />
        </InfoContainer>
        <CalendarDuration value={value} timeStep={timeStep} preparationTime={preparationTime} />
      </Block>
    </MainContainer>
  );
};

export default ServiceFinalDetails;
